import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import type { MarketListingSummary } from '@/types/market';
import { ListingCard, useSavedIds } from './MarketShared';
import styles from './Market.module.scss';

export default function MarketSaved() {
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useSavedIds();
  const [listings, setListings] = useState<MarketListingSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    (async () => {
      setLoading(true);
      try {
        const saved = await apiService.mktGetSaved();
        if (!mounted.current) return;
        setListings(saved);
        setError(null);
      } catch (e: any) {
        if (mounted.current) setError(e?.message || 'Failed to load your saved ads.');
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => { mounted.current = false; };
  }, []);

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <div className={styles.topBar}>
          <button type="button" className={styles.backButton} onClick={() => navigate('/customer/market')}>← Marketplace</button>
          <span className={styles.spacer} />
          <button type="button" className={styles.primaryBtn} onClick={() => navigate('/customer/market/post')}>+ Post an ad</button>
        </div>
        <h1 className={styles.title}>♥ Saved ads</h1>
        <p className={styles.subtitle}>Ads you’ve saved to come back to later.</p>

        {error && <p className={styles.error}>{error}</p>}
        {loading && <p className={styles.muted}><span className={styles.spinner} /> Loading…</p>}

        {!loading && !error && listings.length === 0 && (
          <div className={styles.empty}>
            <div className={styles.emptyIcon}>🤍</div>
            No saved ads yet.
          </div>
        )}

        {!loading && listings.length > 0 && (
          <div className={styles.grid}>
            {listings.map((l) => (
              <ListingCard
                key={l.id}
                listing={l}
                onClick={() => navigate(`/customer/market/listing/${l.id}`)}
                saved={savedIds.has(l.id)}
                onToggleSave={() => toggleSave(l.id)}
              />
            ))}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
